/**
 * Deep-link store (dao-admin-suite) — bl-714 deep-link path restore.
 *
 * Story: authz-001-7a (absorbs bl-714). The hosted-login front-door redirect
 * carries only `return_origin` (the bare suite ORIGIN), so an admin who is
 * bounced to login from a deep path (e.g. `/users/42`) returns to
 * `<origin>/?code=…` — the path is lost (a UX regression vs the legacy
 * `returnUrl=<full URL>` mechanism).
 *
 * The fix is suite-side (the shared auth package owns the redirect and is not
 * touched here): capture the entry deep-link BEFORE the front-door redirect,
 * persist it across the cross-domain round-trip, and restore it AFTER the
 * `?code=`→`/token/exchange` succeeds.
 *
 * `sessionStorage` is used (not the in-memory JWT store) because the redirect
 * navigates the whole document away to the front-door origin and back, which
 * tears down all in-memory state. Only a NON-secret navigation path is stored —
 * never the JWT or the one-time code — so sessionStorage is appropriate.
 */

/** sessionStorage key holding the pre-redirect deep-link path (+ search + hash). */
const DEEP_LINK_KEY = 'hwdao_admin_post_login_path';

/** The `?code=` param name — capture is skipped when it is present (we are returning). */
const CODE_PARAM = 'code';

/**
 * Stash the current `pathname + search + hash` as the post-login destination,
 * unless it is the root path or a `?code=` return. Idempotent and best-effort:
 * a sessionStorage failure (private-mode quota, disabled storage) is swallowed —
 * the worst case is root-landing, i.e. today's behavior.
 *
 * Call this once at app entry, BEFORE the auth gate can redirect to the
 * front-door, so the deep path the admin actually requested is captured.
 */
export function stashDeepLink(): void {
  try {
    const { pathname, search, hash } = window.location;
    // Returning from the front-door (code present) — not a fresh deep-link.
    if (new URLSearchParams(search).has(CODE_PARAM)) return;
    const path = `${pathname}${search}${hash}`;
    // Root has nothing worth restoring; the default post-login landing is root.
    if (pathname === '/' || pathname === '') return;
    window.sessionStorage.setItem(DEEP_LINK_KEY, path);
  } catch {
    // Best-effort — degrade to root-landing (pre-bl-714 behavior).
  }
}

/**
 * Read + clear the stashed deep-link path. Returns the stashed
 * `pathname + search + hash`, or `null` when nothing was stashed (or storage is
 * unavailable). Single-use: clearing on read prevents a stale path from
 * hijacking a later in-app navigation.
 */
export function takeDeepLink(): string | null {
  try {
    const path = window.sessionStorage.getItem(DEEP_LINK_KEY);
    if (path) window.sessionStorage.removeItem(DEEP_LINK_KEY);
    return path && path.length > 0 ? path : null;
  } catch {
    return null;
  }
}

/** Test-only: clear any stashed deep-link between cases. */
export function clearDeepLink(): void {
  try {
    window.sessionStorage.removeItem(DEEP_LINK_KEY);
  } catch {
    // ignore
  }
}
